import { useEffect, useRef } from 'react';

const SCALE_MAX = 4;

interface CrossBrowserTouchEvent extends TouchEvent {
  scale?: number;
}

const distance = (event: TouchEvent) =>
  Math.hypot(
    event.touches[0].pageX - event.touches[1].pageX,
    event.touches[0].pageY - event.touches[1].pageY,
  );

/**
 * Two-finger pinch-to-zoom for an image element, ported from bricks-next
 * `Image`'s `enableZoom`. While two fingers are down it grows and pans the
 * image via `transform` (relative to the start of the gesture), then snaps back
 * on release. Returns a ref to attach to the `<img>`.
 *
 * Driven by raw touch events + transform — no react-native `Animated`.
 */
export const useImagePinchZoom = (enabled: boolean) => {
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;

    const start = { x: 0, y: 0, distance: 0 };

    const handleTouchStart = (event: CrossBrowserTouchEvent) => {
      if (event.touches.length === 2) {
        event.preventDefault();
        start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
        start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
        start.distance = distance(event);
      }
    };

    const handleTouchMove = (event: CrossBrowserTouchEvent) => {
      if (event.touches.length === 2) {
        event.preventDefault();
        // Safari exposes event.scale directly; other browsers derive it.
        const scale = event.scale ?? distance(event) / start.distance;
        const clampedScale = Math.min(Math.max(1, scale), SCALE_MAX);

        // x2 for accelerated panning, matching the bricks behavior.
        const deltaX = ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) * 2;
        const deltaY = ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) * 2;

        el.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${clampedScale})`;
        el.style.zIndex = '9999';
        el.style.transition = 'ease 150ms';
      }
    };

    const handleTouchEnd = () => {
      el.style.transform = '';
      el.style.zIndex = '';
    };

    el.addEventListener('touchstart', handleTouchStart);
    el.addEventListener('touchmove', handleTouchMove);
    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled]);

  return ref;
};
